/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";

import ClayDropDown from "@clayui/drop-down";
import ClayForm, { ClayInput } from "@clayui/form";

import { ErrorMessage, Field, FormikValues, useFormikContext } from "formik";

import styles from "./Controls.module.scss";
import TextError from "./TextError";
import { staticStrings } from "@staticStrings";
import { ContainerLoader } from "@common-components";

interface DynamicTokenInputProps {
  name: string;
  label: string;
  placeholder: string;
  disabled?: boolean;
  required?: boolean;
  dropdownOptions: string[];
  keywordsMap?: Record<string, string>;
  searchOption?: boolean;
  loader?: boolean;
  initialValues: string;
  itemIndex?: number;
}

const DynamicTokenInput: React.FC<DynamicTokenInputProps> = ({
  name,
  label,
  placeholder,
  disabled = false,
  required = false,
  dropdownOptions = [],
  keywordsMap = {},
  searchOption = false,
  loader = false,
  initialValues = "",
  itemIndex
}) => {
  const [inputValue, setInputValue] = useState<string>(initialValues || "");
  const [active, setActive] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [triggerIndex, setTriggerIndex] = useState<number | null>(null);

  const filteredOptions = dropdownOptions?.filter((item) =>
    item.toLowerCase().includes(searchInput.toLowerCase())
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const formik = useFormikContext<FormikValues>();

  // Sync with initialValues prop and formik field value
  useEffect(() => {
    if (initialValues && initialValues !== "") {
      setInputValue(initialValues);
      // Only set initial value if field is undefined (not just falsy)
      if (formik.values[name] === undefined) {
        formik.setFieldValue(name, initialValues);
      }
    }
  }, [initialValues, name, formik]);

  // Sync local state with formik field value changes
  useEffect(() => {
    const fieldValue = formik.values[name];
    if (fieldValue !== inputValue) {
      setInputValue(fieldValue || ""); // Ensure empty string when cleared
    }
  }, [formik.values, name, inputValue]);

  return (
    <>
      {label && (
        <label htmlFor={name} className={styles.labelText}>
          {label}
          {required && (
            <></>
            // <img
            //   src={RequiredIcon}
            //   alt="required"
            //   className={styles.requiredIcon}
            // />
          )}
        </label>
      )}
      <Field name={name}>
        {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
        {({ form, field }: { form: any; field: any }) => {
          // These variables are used in nested functions - ESLint may not detect usage
          const { onTypeShow } = field;
          const handleInputChange = (
            e: React.ChangeEvent<HTMLInputElement>
          ) => {
            const value = e.target.value;
            setInputValue(value);

            // Update both field and formik values
            field.onChange(e); // This handles Formik's internal state
            form.setFieldValue(name, value); // Ensure formik is updated
            form.setFieldTouched(name, true); // Mark field as touched for change tracking

            const cursorPos = e.target.selectionStart ?? 0;
            const lastAtIndex = value?.lastIndexOf("@", cursorPos - 1);

            if (lastAtIndex !== -1 && cursorPos - lastAtIndex === 1) {
              setTriggerIndex(lastAtIndex);
              showDropdown();
            } else {
              setActive(false);
              setTriggerIndex(null);
            }
          };
          const showDropdown = () => {
            if (inputRef.current) {
              setActive(true);
            }
          };
          const handleOptionSelect = (option: string) => {
            if (triggerIndex !== null && inputRef.current) {
              const cursorPos =
                inputRef.current.selectionStart ?? inputValue.length;
              const beforeAt = inputValue.substring(0, triggerIndex);
              const afterCursor = inputValue.substring(cursorPos);
              // Use the value from keywordsMap if available, otherwise use the option (key) itself
              const valueToInsert = keywordsMap[option] || option;
              const newValue = `${beforeAt}{{${valueToInsert}}}${afterCursor}`;

              // Update all states and formik
              setInputValue(newValue);
              form.setFieldValue(name, newValue);
              form.setFieldTouched(name, true); // Mark field as touched
              formik.setFieldValue(name, newValue);

              setActive(false);
              setTriggerIndex(null);
              setSearchInput("");

              // Set cursor after inserted option
              setTimeout(() => {
                const newCursorPos = (beforeAt + `{{${valueToInsert}}}`).length;
                inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
                inputRef.current?.focus();
              }, 0);
            }
          };
          return (
            <ClayForm.Group
              className={`form-group-sm ${
                form.errors[name] && form.touched[name] !== undefined
                  ? "has-error"
                  : ""
              } ${styles.textContainer}`}
            >
              <ClayInput
                name={name}
                ref={inputRef}
                type="text"
                value={field.value ?? inputValue}
                placeholder={placeholder || label}
                className={`${styles.formButton} ${styles.searchInput}`}
                onChange={(e) => {
                  handleInputChange(e);
                }}
                onKeyUp={() => form.setFieldTouched(name)}
                disabled={disabled}
              />
              <ClayDropDown
                active={active}
                closeOnClickOutside={false}
                onActiveChange={setActive}
                trigger={<div></div>}
                className={`${styles.dropDown} ${styles.dynamicDropdown}`}
                menuElementAttrs={{ className: "overflow-auto mw-100" }}
              >
                {searchOption ? (
                  <div className={styles.searchBoxContainer}>
                    {searchInput.length === 0 && (
                      <></>
                      //   <ClayIcon
                      //     className={`${styles.searchIcon} ${styles.dynamicInputSearchIcon}`}
                      //     symbol="search"
                      //     spritemap={Spritemap}
                      //   />
                    )}
                    <ClayDropDown.Search
                      className={`${styles.searchBox} ${searchInput ? styles["searchBox--hasContent"] : ""}`}
                      id={`search_box_${name}`}
                      value={searchInput}
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      placeholder="Search"
                      onChange={(val: string) => setSearchInput(val)}
                    />
                  </div>
                ) : (
                  <></>
                )}
                <ClayDropDown.ItemList
                  items={filteredOptions}
                  className={`${styles.dropDownItemList}`}
                >
                  {loader ? (
                    <div className={styles.containerLoader}>
                      <ContainerLoader msgRequired={false} />
                    </div>
                  ) : (
                    filteredOptions?.map((item) => {
                      return (
                        <ClayDropDown.Item
                          key={item}
                          onClick={() => handleOptionSelect(item)}
                          className={`${styles.dropItem}`}
                        >
                          {item}
                        </ClayDropDown.Item>
                      );
                    })
                  )}
                </ClayDropDown.ItemList>
                {filteredOptions?.length === 0 && (
                  <ClayDropDown.Section
                    className={`${styles.dropDownContainerWidth}`}
                  >
                    <p className={styles.textContent}>
                      {staticStrings.commonTexts.noRecordsFound}
                    </p>
                  </ClayDropDown.Section>
                )}
              </ClayDropDown>
              {!onTypeShow && (
                <ErrorMessage
                  name={name}
                  component={TextError}
                  className={styles.formError}
                />
              )}
            </ClayForm.Group>
          );
        }}
      </Field>
    </>
  );
};
export default DynamicTokenInput;
